import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DocCard } from "@/components/DocCard";
import { AnexoDialog } from "@/components/AnexoDialog";
import { EnviarDocumentoDialog } from "@/components/EnviarDocumentoDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, ArrowLeft, User, Upload, Video } from "lucide-react";

export default function Documents() {
  const [grupos, setGrupos] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [docs, setDocs] = useState(null);
  const [upOpen, setUpOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const destaque = params.get("doc");
  const { user } = useAuth();
  const podeEnviar = user?.role === "administrador" || user?.role === "analista";

  const loadGrupos = useCallback(async () => { const { data } = await api.get("/documents/resumo"); setGrupos(data); }, []);
  const loadDocs = useCallback(async (g) => {
    const { data } = await api.get("/documents", { params: g.cliente_id ? { cliente_id: g.cliente_id } : {} });
    setDocs(g.cliente_id ? data : data.filter((d) => d.application_id === g.application_id));
  }, []);
  useEffect(() => { loadGrupos(); }, [loadGrupos]);
  useEffect(() => {
    const cid = params.get("cliente"), aid = params.get("app");
    if (!grupos || sel || (!cid && !aid)) return;
    const g = grupos.find((x) => (cid && x.cliente_id === cid) || (aid && x.application_id === aid));
    if (g) setSel(g); else if (cid) setSel({ cliente_id: cid, cliente_nome: "Cliente" });
  }, [grupos, params, sel]);
  useEffect(() => {
    if (!docs || !destaque) return;
    const el = document.querySelector(`[data-testid="doc-card-${destaque}"]`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("doc-destaque"); setTimeout(() => el.classList.remove("doc-destaque"), 6000); }
  }, [docs, destaque]);
  useEffect(() => { if (sel) { setDocs(null); loadDocs(sel); } }, [sel, loadDocs]);
  const atualizar = () => { if (sel) loadDocs(sel); loadGrupos(); };

  const filtrados = (grupos || []).filter((g) => !q || g.cliente_nome?.toLowerCase().includes(q.toLowerCase()));
  const cont = (docs || []).reduce((a, d) => { a[d.status || "pendente"] = (a[d.status || "pendente"] || 0) + 1; return a; }, {});

  if (sel) {
    return (
      <div className="space-y-5 fade-up" data-testid="documents-cliente">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" onClick={() => { setSel(null); setParams({}, { replace: true }); }} className="gap-1" data-testid="docs-voltar"><ArrowLeft className="w-4 h-4" /> Clientes</Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold tracking-tight truncate">{sel.cliente_nome}</h1>
            <p className="text-slate-500 text-sm" data-testid="docs-contagem">{cont.pendente || 0} pendente(s) • {cont.aprovado || 0} aprovado(s) • {cont.recusado || 0} recusado(s)</p>
          </div>
          <div className="flex gap-2 flex-wrap ml-auto">
            {sel.cliente_id && podeEnviar && <>
              <Button size="sm" onClick={() => setUpOpen(true)} className="gap-1 bg-onyx hover:bg-black text-white" data-testid="docs-enviar-btn"><Upload className="w-4 h-4" /> Enviar documento</Button>
              <Button size="sm" variant="outline" onClick={() => setVideoOpen(true)} className="gap-1" data-testid="docs-anexar-video"><Video className="w-4 h-4" /> Anexar vídeo (máx. 30s)</Button>
            </>}
            {sel.cliente_id && <Button variant="outline" size="sm" onClick={() => navigate(`/clientes/${sel.cliente_id}`)} className="gap-1" data-testid="docs-ver-cliente"><User className="w-4 h-4" /> Ver cliente</Button>}
          </div>
        </div>
        {!docs ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></div>
          : <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {docs.map((d) => <div key={d.id} className={d.id === destaque ? "rounded-xl ring-2 ring-amber-400 ring-offset-2" : ""} data-testid={d.id === destaque ? "doc-destacado" : undefined}><DocCard d={d} onChange={atualizar} /></div>)}
            {docs.length === 0 && <p className="text-slate-400 text-sm">Nenhum documento.</p>}
          </div>}
        {sel.cliente_id && <>
          <EnviarDocumentoDialog open={upOpen} onClose={() => setUpOpen(false)} clienteId={sel.cliente_id} onDone={atualizar} />
          <AnexoDialog open={videoOpen} onClose={() => setVideoOpen(false)} clienteId={sel.cliente_id} onDone={atualizar} />
        </>}
      </div>
    );
  }

  return (
    <div className="space-y-5 fade-up" data-testid="documents-page">
      <div><h1 className="text-2xl font-extrabold tracking-tight">Documentos</h1><p className="text-slate-500 text-sm">Gestão centralizada: selecione um cliente para enviar, anexar vídeo, aprovar, recusar, substituir ou excluir. Pendentes aparecem primeiro.</p></div>
      <div className="relative max-w-sm"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente..." className="pl-9" data-testid="docs-search" /></div>
      {!grupos ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></div>
        : filtrados.length === 0 ? <p className="text-slate-400 text-sm">Nenhum documento.</p>
        : <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtrados.map((g) => (
            <button type="button" key={g.cliente_id || g.application_id} onClick={() => setSel(g)} className="trt-card p-4 text-left hover:border-gold transition-colors" data-testid={`docs-grupo-${g.cliente_id || g.application_id}`}>
              <div className="font-bold truncate flex items-center gap-2"><User className="w-4 h-4 gold-text" /> {g.cliente_nome}{!g.cliente_id && <span className="text-[10px] uppercase bg-slate-100 px-1.5 rounded">solicitação</span>}</div>
              <div className="mt-2 grid grid-cols-3 gap-1 text-center text-xs">
                <div className={`rounded p-1.5 ${g.pendente ? "bg-amber-50 text-amber-800" : "bg-slate-50 text-slate-400"}`}><div className="font-extrabold text-base">{g.pendente}</div>pendente(s)</div>
                <div className="rounded p-1.5 bg-emerald-50 text-emerald-800"><div className="font-extrabold text-base">{g.aprovado}</div>aprovado(s)</div>
                <div className="rounded p-1.5 bg-red-50 text-red-800"><div className="font-extrabold text-base">{g.recusado}</div>recusado(s)</div>
              </div>
              <div className="text-xs text-slate-400 mt-2">Último envio: {fmtDate(g.ultimo)}</div>
            </button>
          ))}
        </div>}
    </div>
  );
}
